'use client'

import {
  alpha,
  Box,
  Chip,
  Link,
  Popover,
  Stack,
  type Theme,
} from '@mui/material'
import { ArrowDropDownIcon, CloseIcon } from 'icons'
import { useState, type MouseEvent, type ReactNode, type Ref } from 'react'
import { Announcer } from '../../tools/announcer'

// Sized and shaped to sit flush next to a SearchField in a filter row (same
// height, same 4px radius), so the trigger reads as a field, not a pill.
//
// Themes may skin MuiChip for meta-pills (the teach redesign gives
// colorPrimary chips a gradient fill and outlined chips a shadow, via
// two-class styleOverrides selectors). The state styles below repeat the
// same `&.MuiChip-…` selectors so they match that specificity and win.
const triggerChipSx = {
  fontWeight: 600,
  borderRadius: 1,
  '&.MuiChip-outlined': { boxShadow: 'none' },
  fontSize: '0.75rem',
  // Pinned for the same reason as fontSize: a theme whose chip skin is an uppercase micro-label
  // (TAP's) would otherwise shout the filter's own label back at the user.
  textTransform: 'none',
  height: 28,
  '& .MuiChip-deleteIcon': { fontSize: 16 },
  // MUI centres chip content, so any chip wider than its content would float
  // the caret/clear mid-chip. Growing the label pins it to the right edge.
  '& .MuiChip-label': { flexGrow: 1 },
}

// `field.border` is a teach palette key; other themes fall back to MUI's outlined-input border.
interface PaletteWithField {
  field?: { border: string }
}

// The neutral, outlined-default look: matches the outlined TextField next to it
// with the SearchField placeholder's label color.
const neutralChipSx = {
  '&.MuiChip-outlinedDefault': {
    backgroundColor: 'background.paper',
    borderColor: (theme: Theme) =>
      (theme.palette as PaletteWithField).field?.border ??
      'rgba(0, 0, 0, 0.23)',
    color: 'text.secondary',
    '&:hover': { backgroundColor: 'action.hover' },
  },
}

// Fills the row so the trigger reads as a full-width field instead of a
// content-sized pill.
const fullWidthChipSx = { width: '100%' }

// The shared shell for chip-style filter dropdowns: a trigger that doubles as
// the active-state indicator, plus a popover panel. With `onClear` the active
// chip's trailing icon is an X that clears (and the panel gains a "Clear" link
// unless `showClearLink` is false); without it that icon is a caret that just
// opens the panel, so the chip always keeps a value.
//
// - No selection (`summary === null`): neutral chip with the placeholder label
//   and a dropdown caret; clicking anywhere opens the panel.
// - Active selection: the chip switches to the primary-outlined style and shows
//   the summary.
//
// `children` may be a render function receiving `close`, for panels that
// should close themselves after a pick (e.g. a single-select), and whether the
// panel was opened from the keyboard.
export function FilterDropdown({
  label,
  summary,
  onClear,
  children,
  fullWidth = false,
  highlightSelected = true,
  showClearLink = true,
  onClose,
  width,
  disabled = false,
  triggerRef,
}: {
  label: string
  summary: string | null
  // Omit to drop the clear affordances entirely (no chip X, no "Clear" link) —
  // for single-select pickers that should always hold a value.
  onClear?: () => void
  children:
    | ReactNode
    | ((close: () => void, panel: { openedWithKeyboard: boolean }) => ReactNode)
  // Stretch the trigger to fill its container (label left, caret/clear right).
  fullWidth?: boolean
  // When false, an active selection keeps the neutral look instead of the
  // primary wash/border — for selectors where a pick isn't a "filter on" signal.
  highlightSelected?: boolean
  // The chip's X (from onClear) is the primary clear; set false to drop the
  // redundant "Clear" link from the panel while keeping the X.
  showClearLink?: boolean
  // Fired whenever the panel is dismissed (Escape, backdrop, or a child calling
  // `close`). Panels that start work on selection need this to unwind it —
  // relying on their own unmount instead ties the cleanup to the exit
  // transition finishing.
  onClose?: () => void
  // Fixes the trigger chip's width (not the panel's), so it doesn't resize with
  // the summary — a long one ellipsizes instead. Ignored under fullWidth, which
  // already fills the row.
  width?: number | string
  // Greys the trigger out, takes it out of the tab order and sets aria-disabled.
  disabled?: boolean
  // For a consumer that must put focus back on the trigger itself, e.g. after a
  // navigation remounts it.
  triggerRef?: Ref<HTMLDivElement>
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  // Keys the panel content so it remounts on every open. Reopening inside the
  // previous close transition would otherwise keep the old content mounted,
  // and focus only moves into a list when it mounts.
  const [openCount, setOpenCount] = useState(0)
  const [openedWithKeyboard, setOpenedWithKeyboard] = useState(false)
  const isOpen = anchorEl !== null
  // A disabled Chip only blocks pointer events in CSS, so a scripted click would still open it.
  // MUI's ButtonBase hands onClick the key event itself when Enter or Space activates the
  // chip, so only a pointer open arrives as a 'click'.
  const open = (el: HTMLElement | null, event: { type: string }) => {
    if (disabled) return
    setAnchorEl(el)
    setOpenedWithKeyboard(event.type !== 'click')
    setOpenCount((count) => count + 1)
  }
  const close = () => {
    setAnchorEl(null)
    onClose?.()
  }

  // The truncation needs no CSS of its own: once the chip can't grow, MUI's
  // label already carries overflow/text-overflow, and its `overflow: hidden`
  // zeroes the flex auto-minimum so it actually shrinks. maxWidth only guards a
  // container narrower than `width`.
  const widthSx =
    width === undefined || fullWidth ? [] : [{ width, maxWidth: '100%' }]

  return (
    <>
      {summary === null ? (
        <Chip
          ref={triggerRef}
          label={label}
          variant="outlined"
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          sx={[
            triggerChipSx,
            neutralChipSx,
            ...(fullWidth ? [fullWidthChipSx] : []),
            ...widthSx,
          ]}
          onClick={(event) => {
            open(event.currentTarget, event)
          }}
          // The caret is purely decorative — clicking it opens the panel, the
          // same as the rest of the chip. Anchor on the chip root (not the
          // icon) so the panel aligns identically for both click targets.
          onDelete={(event: MouseEvent<HTMLElement>) => {
            open(
              event.currentTarget.closest<HTMLElement>('.MuiChip-root'),
              event
            )
          }}
          deleteIcon={<ArrowDropDownIcon />}
        />
      ) : (
        <Chip
          ref={triggerRef}
          label={summary}
          variant="outlined"
          disabled={disabled}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          // A barely-there primary wash + solid primary border marks the filter
          // as active. With highlightSelected off the trigger stays neutral, so
          // a pick reads as a field value, not a "filter on" signal.
          color={highlightSelected ? 'primary' : undefined}
          sx={
            highlightSelected
              ? [
                  triggerChipSx,
                  (theme) => ({
                    '&.MuiChip-colorPrimary': {
                      // `background` (not backgroundColor) so it also clears any
                      // gradient background-image a theme sets on primary chips.
                      background: alpha(theme.palette.primary.main, 0.1),
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.dark,
                      '&:hover': {
                        background: alpha(theme.palette.primary.main, 0.16),
                      },
                      // The clear X matches the SearchField's clear IconButton
                      // (action.active) instead of the primary delete-icon color.
                      '& .MuiChip-deleteIcon': {
                        color: theme.palette.action.active,
                        '&:hover': { color: theme.palette.text.primary },
                      },
                    },
                  }),
                  ...(fullWidth ? [fullWidthChipSx] : []),
                  ...widthSx,
                ]
              : [
                  triggerChipSx,
                  neutralChipSx,
                  ...(fullWidth ? [fullWidthChipSx] : []),
                  ...widthSx,
                ]
          }
          onClick={(event) => {
            open(event.currentTarget, event)
          }}
          // With a clear handler the trailing icon is an X that clears; without
          // one it's a decorative caret that opens the panel, the same as the
          // neutral trigger, so the chip keeps holding a value.
          // Disabled drops the clear X but keeps the decorative caret, which open() already guards.
          onDelete={
            onClear
              ? disabled
                ? undefined
                : onClear
              : (event: MouseEvent<HTMLElement>) => {
                  open(
                    event.currentTarget.closest<HTMLElement>('.MuiChip-root'),
                    event
                  )
                }
          }
          deleteIcon={onClear ? <CloseIcon /> : <ArrowDropDownIcon />}
        />
      )}

      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            // The panel holds more than its list (a search box, a Clear link,
            // footer links), so it is a dialog named by the filter, not a menu.
            role: 'dialog',
            'aria-label': label,
            // mt: breathing room between the trigger and the panel. p: 0 undoes
            // any theme Paper padding so the Clear underline and the section
            // dividers run edge to edge; rows carry their own horizontal padding.
            sx: { mt: 0.5, p: 0 },
          },
        }}
      >
        {/* The Popover aria-hides the rest of the page, including the root live region, so a
            result count that changes while a panel stays open (ticking grades) needs a region
            in here to be spoken. */}
        {isOpen ? <Announcer /> : null}
        <Box key={openCount} sx={{ py: 1, minWidth: 220 }}>
          {/* Clear sits in its own section above the options. */}
          {onClear && showClearLink && summary !== null ? (
            <Stack
              direction="row"
              justifyContent="flex-start"
              sx={{
                px: 2,
                pb: 1,
                mb: 0.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Link
                component="button"
                type="button"
                variant="body2"
                underline="none"
                onClick={onClear}
              >
                Clear
              </Link>
            </Stack>
          ) : null}
          {typeof children === 'function'
            ? children(close, { openedWithKeyboard })
            : children}
        </Box>
      </Popover>
    </>
  )
}
